/**
 * Scenario authoring API (completion plan P5 enabler).
 *
 * GET/PUT/DELETE /scenarios/:id/progression, GET /rubrics, and tag edits
 * through the existing PUT /scenarios/:id. The server gates every write on a
 * Central platform admin holding practice:write (the same authority as
 * scenario create/update) and validates the definition; a 422 carries the
 * problems the builder shows inline.
 */

import { api } from '../api';
import type {
  AssessmentUnavailableReason,
  ProgressionAuthoringView,
  ProgressionDefinition,
  RubricSummary,
} from '../../components/scenarioAuthoring/progressionTypes';

/**
 * GET /scenarios/:id/assessment-preview — the authoring/QA preview of what an
 * attempt would be held to. Never an attempt: no session, result, XP, badge,
 * portfolio item or marketplace signal; and it carries no score (UNKNOWN is
 * not a score, UNAVAILABLE is not 0). Same authority as progression authoring.
 */
export interface AssessmentPreviewEvidence {
  evidence: string;
  state: 'available' | 'unavailable';
  evidenceClass?: 'UNAVAILABLE';
  reason?: string;
  explanation?: string;
}

export interface AssessmentPreviewModality {
  modality: string;
  offeredByServer: boolean;
  gradable: boolean;
  unavailableReason: AssessmentUnavailableReason | null;
  rubric: { id: string; version: string; title: string; scope: string } | null;
  requiredEvidence: AssessmentPreviewEvidence[];
  evidenceNotJudged: string[];
  dimensions: { key: string; label: string; description: string; presentation: 'scored_from_evidence_or_not_measured' }[];
  overall: { minScoredForOverall: number; whenInsufficient: 'UNKNOWN' } | null;
  observableEvidence: {
    key: string;
    label: string;
    includedInOverall: false;
    state: 'available' | 'unavailable';
    evidenceClass: string;
    explanation: string | null;
  }[];
}

export interface AssessmentPreview {
  notAnAttempt: {
    kind: 'authoring_preview';
    createsSession: false;
    recordsResult: false;
    countsAsAttempt: false;
    awardsProgress: false;
    portfolioArtifact: false;
    marketplaceSignal: false;
  };
  scenario: { id: string; title: string | null; type: string | null; difficulty: string | null; persona: string; tags: string[] };
  contentStatus: 'complete' | 'content_incomplete';
  validation: { valid: boolean; problems: string[] };
  testing: { available: boolean; reason: string | null };
  modalities: AssessmentPreviewModality[];
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const scenarioAuthoringApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScenarioProgression: builder.query<ProgressionAuthoringView, string>({
      query: (scenarioId) => `/scenarios/${scenarioId}/progression`,
      transformResponse: (r: ApiEnvelope<ProgressionAuthoringView>) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'ScenarioProgression', id }],
    }),

    saveScenarioProgression: builder.mutation<
      ProgressionAuthoringView,
      { scenarioId: string; definition: ProgressionDefinition }
    >({
      query: ({ scenarioId, definition }) => ({
        url: `/scenarios/${scenarioId}/progression`,
        method: 'PUT',
        body: { definition },
      }),
      transformResponse: (r: ApiEnvelope<ProgressionAuthoringView>) => r.data,
      invalidatesTags: (_r, _e, { scenarioId }) => [{ type: 'ScenarioProgression', id: scenarioId }],
    }),

    deleteScenarioProgression: builder.mutation<ProgressionAuthoringView, string>({
      query: (scenarioId) => ({ url: `/scenarios/${scenarioId}/progression`, method: 'DELETE' }),
      transformResponse: (r: ApiEnvelope<ProgressionAuthoringView>) => r.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'ScenarioProgression', id }],
    }),

    updateScenarioTags: builder.mutation<unknown, { scenarioId: string; tags: string[] }>({
      query: ({ scenarioId, tags }) => ({ url: `/scenarios/${scenarioId}`, method: 'PUT', body: { tags } }),
      invalidatesTags: (_r, _e, { scenarioId }) => [
        { type: 'ScenarioProgression', id: scenarioId },
        { type: 'Scenarios', id: scenarioId },
        'Scenarios',
      ],
    }),

    // Reuses the progression tag: saving or removing a progression refreshes the preview.
    getScenarioAssessmentPreview: builder.query<AssessmentPreview, string>({
      query: (scenarioId) => `/scenarios/${scenarioId}/assessment-preview`,
      transformResponse: (r: ApiEnvelope<AssessmentPreview>) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'ScenarioProgression', id }],
    }),

    listRubrics: builder.query<RubricSummary[], void>({
      query: () => '/rubrics',
      transformResponse: (r: ApiEnvelope<RubricSummary[]>) => r.data,
      providesTags: ['Rubrics'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetScenarioProgressionQuery,
  useSaveScenarioProgressionMutation,
  useDeleteScenarioProgressionMutation,
  useUpdateScenarioTagsMutation,
  useListRubricsQuery,
  useGetScenarioAssessmentPreviewQuery,
} = scenarioAuthoringApi;
